import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

/**
 * Bottom of an endless list: loads the next chunk when it scrolls into view
 * (with a button as a fallback) and says how much of the list is showing.
 */
export function LoadMore({
  shown,
  total,
  hasMore,
  loading,
  onLoadMore,
  noun = 'questions',
}: {
  shown: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  noun?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef({ hasMore, loading, onLoadMore });
  latest.current = { hasMore, loading, onLoadMore };

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    // Start loading a little before the end comes into view.
    const io = new IntersectionObserver(
      (entries) => {
        const { hasMore: more, loading: busy, onLoadMore: load } = latest.current;
        if (entries.some((e) => e.isIntersecting) && more && !busy) load();
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (total === 0) return null;
  return (
    <Box ref={ref} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      {loading && <CircularProgress size={24} aria-label={`Loading more ${noun}`} />}
      {hasMore && !loading && (
        <Button size="small" onClick={onLoadMore}>
          Load more
        </Button>
      )}
      <Typography variant="body2" color="text.secondary">
        {hasMore ? `Showing ${shown} of ${total} ${noun}` : `All ${total} ${noun} shown`}
      </Typography>
    </Box>
  );
}
